import { useState } from 'react'
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  ArrowDown,
  ArrowLeft,
  ArrowLeftRight,
  CheckCheck,
  Info,
  RotateCcw,
  LineChart,
  Repeat,
  Target,
  SlidersHorizontal,
  Archive as ArchiveIcon,
  CreditCard,
  Pencil,
  Plus,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react'
import {
  getBalanceHistory,
  isPlainAccount,
  isTransactable,
  RECONCILABLE_TYPES,
  listAccounts,
  supportsValuationHistory,
  updateAccount,
  type AccountSummary,
} from '@/api/accounts'
import { listCategories } from '@/api/categories'
import { getEarmarks } from '@/api/goals'
import { listRecurring } from '@/api/recurring'
import { listPersonalLoans } from '@/api/personalLoans'
import { PERSONAL_LOANS_ENABLED } from '@/lib/featureFlags'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { WIDE_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { listValuations, recordValuation } from '@/api/valuations'
import { ArchiveAccountDialog } from '@/components/accounts/ArchiveAccountDialog'
import {
  BalanceChart,
  BalancePreview,
  ValueChart,
} from '@/components/accounts/detail/AccountCharts'
import { AccountHero } from '@/components/accounts/detail/AccountHero'
import {
  AboutAccount,
  AccountGoals,
  AccountRepeats,
  SectionHeader,
  ValueUpdates,
} from '@/components/accounts/detail/AccountSections'
import { BillCategories } from '@/components/accounts/detail/BillCategories'
import { ReconcileModal } from '@/components/accounts/detail/ReconcileModal'
import { PersonActionsMenu, type PersonAction } from '@/components/people/PersonActionsMenu'
import { AddTransactionModal } from '@/components/transactions/TransactionForms'
import type { AddKind } from '@/components/transactions/add/addKinds'
import { DatePicker } from '@/components/DatePicker'
import { TransactionList } from '@/components/transactions/TransactionList'
import { useTransactionPages } from '@/hooks/useTransactionPages'
import { currentMonth } from '@/lib/transactionDateRangeFilter'
import { ALL_TIME, currentPath, transactionsLink } from '@/lib/transactionsLink'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { IconPicker } from '@/components/IconPicker'
import { useHouseholdMembers } from '@/hooks/useHouseholdMembers'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { formatDate, formatShortDate } from '@/lib/formatDate'
import { ACCOUNT_ICON_OPTIONS } from '@/lib/accountIconOptions'
import { ROUTES } from '@/routes/routes'
import { RecurringModal } from './RecurringPage'

const LABEL_CLS = 'block text-sm font-medium text-[var(--fl-text-1)] mb-1'

// ── Correct Balance Modal ──────────────────────────────────────────────────

const correctBalanceSchema = z.object({
  newBalance: z.coerce.number(),
  bookTransaction: z.boolean(),
  date: z.string().optional(),
  description: z.string().optional(),
  categoryId: z.string().optional(),
  notes: z.string().optional(),
})
type CorrectBalanceForm = z.infer<typeof correctBalanceSchema>

function CorrectBalanceModal({
  account,
  onClose,
}: {
  account: AccountSummary
  onClose: () => void
}) {
  const { symbol, format } = useFormatCurrency()
  const queryClient = useQueryClient()
  // CREDIT_CARD / PAYABLE store the balance negative (money owed); this modal works in
  // positive "amount owed" terms and re-signs on submit, matching the Add Account form.
  const owedMode = account.type === 'CREDIT_CARD' || account.type === 'PAYABLE'
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => listCategories(),
  })
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CorrectBalanceForm>({
    resolver: zodResolver(correctBalanceSchema) as Resolver<CorrectBalanceForm>,
    defaultValues: {
      // CREDIT_CARD / PAYABLE balances are stored negative (money owed); the field shows
      // and takes the amount owed as a positive number and it's re-signed on submit.
      newBalance: owedMode ? -account.balance : account.balance,
      bookTransaction: true,
      date: new Date().toISOString().split('T')[0],
    },
  })

  const bookTransaction = watch('bookTransaction')
  const newBalance = watch('newBalance')
  const signedNewBalance = owedMode ? -Math.abs(Number(newBalance)) : Number(newBalance)
  const diff = signedNewBalance - account.balance
  const negativeOwed = owedMode && Number(newBalance) < 0

  const mutation = useMutation({
    mutationFn: (v: CorrectBalanceForm) => {
      const target = owedMode ? -Math.abs(v.newBalance) : v.newBalance
      if (v.bookTransaction) {
        if (!v.description || !v.date) throw new Error('Description and date are required')
        return updateAccount(account.id, {
          newBalance: target,
          date: v.date,
          description: v.description,
          categoryId: v.categoryId || undefined,
          notes: v.notes || undefined,
        })
      }
      const delta = target - account.balance
      return updateAccount(account.id, {
        newOpeningBalance: account.openingBalance + delta,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      onClose()
    },
  })

  function onSubmit(v: CorrectBalanceForm) {
    if (negativeOwed) return
    if (v.bookTransaction && (!v.description || !v.date)) return
    mutation.mutate(v)
  }

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 overflow-y-auto"
    >
      <div className="bg-[var(--fl-surface)] rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-md mx-4 my-8 p-7">
        <h2
          className="text-[22px] font-semibold text-[var(--fl-heading)] mb-1"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        >
          Correct Balance
        </h2>
        <p className="text-sm text-[var(--fl-text-3)] mb-5">
          {owedMode
            ? 'Set this to what you currently owe on this account.'
            : 'Set the balance to what it should actually be.'}
        </p>

        <div className="bg-[var(--fl-surface-subtle)] rounded-xl px-4 py-3 mb-5 flex items-center justify-between text-sm">
          <span className="text-[var(--fl-text-3)]">
            {owedMode ? 'Currently owed' : 'Current balance'}
          </span>
          <div className="flex items-center gap-3">
            <span className="font-semibold text-[var(--fl-text-1)]">
              {format(owedMode ? -account.balance : account.balance)}
            </span>
            {!isNaN(diff) && diff !== 0 && (
              <span
                className={`font-semibold ${diff > 0 ? 'text-[var(--fl-income)]' : 'text-[var(--fl-error)]'}`}
              >
                {diff > 0 ? '+' : ''}
                {format(diff)}
              </span>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className={LABEL_CLS} htmlFor="newBalance">
              {owedMode ? `Amount owed now (${symbol})` : `Correct balance (${symbol})`}
            </label>
            <Input id="newBalance" {...register('newBalance')} type="number" step="0.01" />
            {owedMode && (
              <p className="text-xs text-[var(--fl-text-3)] mt-1">
                Enter what you owe as a positive number.
              </p>
            )}
            {negativeOwed && (
              <p className="text-xs text-[var(--fl-error)] mt-1">
                Enter the amount owed as a positive number (use 0 if it&rsquo;s cleared).
              </p>
            )}
            {errors.newBalance && (
              <p className="text-xs text-[var(--fl-error)] mt-1">{errors.newBalance.message}</p>
            )}
          </div>

          <label className="flex items-center justify-between gap-3 p-4 rounded-xl border border-[var(--fl-border)] cursor-pointer hover:bg-[var(--fl-surface-subtle)] transition-colors">
            <div>
              <p className="text-sm font-medium text-[var(--fl-text-1)]">
                Create adjustment transaction
              </p>
              <p className="text-xs text-[var(--fl-text-3)] mt-0.5">
                {bookTransaction
                  ? 'Difference will appear in your transaction ledger'
                  : 'Balance corrected silently — no transaction created'}
              </p>
            </div>
            <Checkbox {...register('bookTransaction')} />
          </label>

          {bookTransaction && (
            <>
              <div>
                <label className={LABEL_CLS}>Date</label>
                <Controller
                  control={control}
                  name="date"
                  render={({ field }) => (
                    <DatePicker value={field.value ?? ''} onChange={field.onChange} />
                  )}
                />
              </div>
              <div>
                <label className={LABEL_CLS} htmlFor="desc">
                  Description
                </label>
                <Input
                  id="desc"
                  {...register('description')}
                  placeholder="e.g. Monthly reconciliation"
                />
                {errors.description && (
                  <p className="text-xs text-[var(--fl-error)] mt-1">
                    {errors.description.message}
                  </p>
                )}
              </div>
              <div>
                <label className={LABEL_CLS} htmlFor="cat">
                  Category (optional)
                </label>
                <Controller
                  control={control}
                  name="categoryId"
                  render={({ field }) => (
                    <Select
                      id="cat"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      searchable
                    >
                      <option value="">None</option>
                      {categoriesData?.categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </div>
              <div>
                <label className={LABEL_CLS} htmlFor="notes">
                  Notes (optional)
                </label>
                <Textarea id="notes" {...register('notes')} rows={2} className="resize-none" />
              </div>
            </>
          )}

          {mutation.isError && (
            <p className="text-xs text-[var(--fl-error)]">
              {(mutation.error as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail ?? 'Something went wrong. Please try again.'}
            </p>
          )}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || mutation.isPending || negativeOwed}
              className="flex-1 font-semibold"
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Edit Account Modal (name + icon) ───────────────────────────────────────

const editAccountSchema = z.object({
  name: z.string().min(1, 'Name is required').max(50),
  icon: z.string().optional(),
})
type EditAccountForm = z.infer<typeof editAccountSchema>

function EditAccountModal({ account, onClose }: { account: AccountSummary; onClose: () => void }) {
  const queryClient = useQueryClient()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<EditAccountForm>({
    resolver: zodResolver(editAccountSchema) as Resolver<EditAccountForm>,
    defaultValues: { name: account.name, icon: account.icon ?? '' },
  })

  const mutation = useMutation({
    mutationFn: (v: EditAccountForm) =>
      updateAccount(account.id, { name: v.name, icon: v.icon || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      onClose()
    },
  })

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 overflow-y-auto"
    >
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-md mx-4 my-8 p-6">
        <h2
          className="text-[24px] font-semibold text-[var(--fl-heading)] mb-5"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        >
          Edit Account
        </h2>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div>
            <label className={LABEL_CLS} htmlFor="editAccountName">
              Account Name
            </label>
            <Input id="editAccountName" {...register('name')} />
            {errors.name && (
              <p className="text-xs text-[var(--fl-error)] mt-1">{errors.name.message}</p>
            )}
          </div>
          <div>
            <label className={LABEL_CLS}>Icon (optional)</label>
            <Controller
              control={control}
              name="icon"
              render={({ field }) => (
                <IconPicker
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  options={ACCOUNT_ICON_OPTIONS}
                />
              )}
            />
          </div>
          {mutation.isError && (
            <p className="text-xs text-[var(--fl-error)]">
              {(mutation.error as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail ?? 'Something went wrong. Please try again.'}
            </p>
          )}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || mutation.isPending}
              className="flex-1 font-semibold"
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Credit Card Settings Modal ─────────────────────────────────────────────

const creditCardSettingsSchema = z.object({
  creditLimit: z.coerce.number().positive('Credit limit must be positive'),
  billingDate: z.string().optional(),
})
type CreditCardSettingsForm = z.infer<typeof creditCardSettingsSchema>

function CreditCardSettingsModal({
  account,
  onClose,
}: {
  account: AccountSummary
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreditCardSettingsForm>({
    resolver: zodResolver(creditCardSettingsSchema) as Resolver<CreditCardSettingsForm>,
    defaultValues: {
      creditLimit: account.creditLimit ?? 0,
      billingDate: account.billingDate ? String(account.billingDate) : '',
    },
  })

  const mutation = useMutation({
    mutationFn: (v: CreditCardSettingsForm) =>
      updateAccount(account.id, {
        creditLimit: v.creditLimit,
        ...(v.billingDate ? { billingDate: Number(v.billingDate) } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      onClose()
    },
  })

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 overflow-y-auto"
    >
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-md mx-4 my-8 p-6">
        <h2
          className="text-[24px] font-semibold text-[var(--fl-heading)] mb-5"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        >
          Edit Settings
        </h2>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div>
            <label className={LABEL_CLS} htmlFor="creditLimit">
              Credit Limit
            </label>
            <Input id="creditLimit" {...register('creditLimit')} type="number" step="0.01" />
            {errors.creditLimit && (
              <p className="text-xs text-[var(--fl-error)] mt-1">{errors.creditLimit.message}</p>
            )}
          </div>
          <div>
            <label className={LABEL_CLS} htmlFor="billingDate">
              Billing Date
            </label>
            <Controller
              control={control}
              name="billingDate"
              render={({ field }) => (
                <Select
                  id="billingDate"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  <option value="">Select a day</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </Select>
              )}
            />
            {errors.billingDate && (
              <p className="text-xs text-[var(--fl-error)] mt-1">{errors.billingDate.message}</p>
            )}
          </div>
          {mutation.isError && (
            <p className="text-xs text-[var(--fl-error)]">
              {(mutation.error as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail ?? 'Something went wrong. Please try again.'}
            </p>
          )}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || mutation.isPending}
              className="flex-1 font-semibold"
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Update Value Modal ─────────────────────────────────────────────────────

const recordValuationSchema = z.object({
  currentValue: z.coerce.number().nonnegative('Current value cannot be negative'),
  asOfDate: z.string().min(1, 'Date is required'),
})
type RecordValuationForm = z.infer<typeof recordValuationSchema>

function UpdateValueModal({ account, onClose }: { account: AccountSummary; onClose: () => void }) {
  const { format } = useFormatCurrency()
  const queryClient = useQueryClient()
  const { data: valuations } = useQuery({
    queryKey: ['valuations', account.id],
    queryFn: () => listValuations(account.id),
  })
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<RecordValuationForm>({
    resolver: zodResolver(recordValuationSchema) as Resolver<RecordValuationForm>,
    defaultValues: {
      currentValue: account.currentValue ?? account.balance,
      asOfDate: new Date().toISOString().split('T')[0],
    },
  })

  const mutation = useMutation({
    mutationFn: (v: RecordValuationForm) =>
      recordValuation(account.id, { currentValue: v.currentValue, asOfDate: v.asOfDate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['valuations', account.id] })
      onClose()
    },
  })

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 overflow-y-auto"
    >
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-md mx-4 my-8 p-6">
        <h2
          className="text-[24px] font-semibold text-[var(--fl-heading)] mb-5"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        >
          Update Value
        </h2>
        {valuations && valuations.length > 0 && (
          <div className="mb-5 pb-4 border-b border-[var(--fl-surface-subtle)] max-h-40 overflow-y-auto">
            <span className="text-xs text-[var(--fl-text-2)] block mb-2">History</span>
            <div className="divide-y divide-[var(--fl-surface-subtle)]">
              {valuations.map((v) => (
                <div key={v.id} className="py-2 flex items-center justify-between text-sm">
                  <span className="text-[var(--fl-text-2)]">{formatDate(v.asOfDate)}</span>
                  <span className="font-medium text-[var(--fl-text-1)]">
                    {format(v.currentValue)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div>
            <label className={LABEL_CLS} htmlFor="currentValue">
              Current Value
            </label>
            <Input id="currentValue" {...register('currentValue')} type="number" step="0.01" />
            {errors.currentValue && (
              <p className="text-xs text-[var(--fl-error)] mt-1">{errors.currentValue.message}</p>
            )}
          </div>
          <div>
            <label className={LABEL_CLS}>As of Date</label>
            <Controller
              control={control}
              name="asOfDate"
              render={({ field }) => (
                <DatePicker value={field.value ?? ''} onChange={field.onChange} />
              )}
            />
            {errors.asOfDate && (
              <p className="text-xs text-[var(--fl-error)] mt-1">{errors.asOfDate.message}</p>
            )}
          </div>
          {mutation.isError && (
            <p className="text-xs text-[var(--fl-error)]">
              {(mutation.error as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail ?? 'Something went wrong. Please try again.'}
            </p>
          )}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || mutation.isPending}
              className="flex-1 font-semibold"
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────

const ACTION_BTN =
  'inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border px-2 text-[13px] font-semibold lg:h-11 lg:px-4'
const PRIMARY_BTN = `${ACTION_BTN} border-[var(--fl-primary)] bg-[var(--fl-primary)] text-[var(--fl-primary-fg)] hover:bg-[var(--fl-primary-hover)]`
const SECONDARY_BTN = `${ACTION_BTN} border-[var(--fl-border)] bg-[var(--fl-surface)] text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]`

type AddOpen = { kind?: AddKind; transferInto?: boolean }

const PHONE_TX_LIMIT = 8

// Names only tell accounts apart when some members are left out; first names keep the line short.
function ownerSummary(names: string[], memberCount: number): string | null {
  if (memberCount < 2 || names.length === 0 || names.length >= memberCount) return null
  const first = names.map((n) => n.split(' ')[0])
  return first.length > 2
    ? `${first.slice(0, 2).join(', ')} +${first.length - 2}`
    : first.join(', ')
}

/** Where the account stands against its bank; opens the reconcile check. */
function ReconcileChip({ account, onOpen }: { account: AccountSummary; onOpen?: () => void }) {
  const r = account.reconciliation
  if (!r && !onOpen) return null
  const recheck = r?.status === 'RECHECK'
  const label = !r
    ? 'Reconcile'
    : recheck
      ? 'Re-check balance'
      : `Reconciled · ${formatShortDate(r.asOf)}${r.newSince > 0 ? ` · ${r.newSince} new` : ''}`
  const Icon = recheck ? RotateCcw : CheckCheck
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      className={`flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium disabled:cursor-default ${
        recheck
          ? 'border-[var(--fl-warning-text)] bg-[var(--fl-warning-bg)] text-[var(--fl-warning-dark)]'
          : r
            ? 'border-[var(--fl-border)] bg-[var(--fl-surface)] text-[var(--fl-primary-text)] enabled:hover:bg-[var(--fl-surface-hover)]'
            : 'border-[var(--fl-border)] bg-[var(--fl-surface)] text-[var(--fl-text-2)] enabled:hover:bg-[var(--fl-surface-hover)]'
      }`}
    >
      <Icon size={13} aria-hidden="true" />
      {label}
    </button>
  )
}

export default function AccountDetailPage() {
  const isReadOnly = useIsReadOnly()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const members = useHouseholdMembers()
  const [showEditAccount, setShowEditAccount] = useState(false)
  const [showCorrectBalance, setShowCorrectBalance] = useState(false)
  const [showCreditCardSettings, setShowCreditCardSettings] = useState(false)
  const [showUpdateValue, setShowUpdateValue] = useState(false)
  const [showAddRecurring, setShowAddRecurring] = useState(false)
  const [adding, setAdding] = useState<AddOpen | null>(null)
  const [confirmArchive, setConfirmArchive] = useState(false)
  const [reconciling, setReconciling] = useState(false)
  const location = useLocation()

  const { data, isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => listAccounts(),
  })
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => listCategories(),
    enabled: adding !== null,
  })

  const txPages = useTransactionPages(
    { financialAccountId: id, month: currentMonth() },
    { enabled: !!id }
  )

  const account = id ? data?.accounts.find((a) => a.id === id) : undefined
  const canBeLoan =
    PERSONAL_LOANS_ENABLED && (account?.type === 'RECEIVABLE' || account?.type === 'PAYABLE')
  const { data: personalLoans } = useQuery({
    queryKey: ['personal-loans', 'all'],
    queryFn: () => listPersonalLoans(),
    enabled: canBeLoan,
  })
  // A personal loan's balance moves only through its payments; the server rejects adjustments.
  const isPersonalLoan = canBeLoan && !!personalLoans?.some((l) => l.accountId === id)
  const plain = !!account && isPlainAccount(account.type)
  const { data: earmarks } = useQuery({
    queryKey: ['earmarks', id],
    queryFn: () => getEarmarks(id!),
    enabled: plain,
  })
  const valued = !!account && supportsValuationHistory(account.type)
  const { data: valuations } = useQuery({
    queryKey: ['valuations', id],
    queryFn: () => listValuations(id!),
    enabled: valued,
  })
  const wide = useMediaQuery(WIDE_QUERY)
  const charted = !!account && !valued && isTransactable(account.type)
  // Same queries as the sections below, so the phone's jump chips only name sections that render.
  const { data: balanceHistory } = useQuery({
    queryKey: ['balance-history', id, 6],
    queryFn: () => getBalanceHistory(id!, 6),
    enabled: charted && !wide,
  })
  const { data: recurringData } = useQuery({
    queryKey: ['recurring'],
    queryFn: () => listRecurring(),
    enabled: !!account && !wide,
  })

  if (isLoading) {
    return <div className="p-6 text-[var(--fl-text-2)] text-sm">Loading…</div>
  }

  if (!account) {
    return (
      <div className="p-6">
        <p className="text-[var(--fl-error)] text-sm">Account not found.</p>
        <Button
          variant="link"
          onClick={() => navigate(ROUTES.APP.ACCOUNTS)}
          className="mt-2 h-auto p-0 text-[var(--fl-income)]"
        >
          Back to Accounts
        </Button>
      </div>
    )
  }

  const ownerNames = account.ownerMemberIds
    .map((memberId) => members.find((m) => m.id === memberId)?.name)
    .filter((name): name is string => !!name)

  const spendable = ['BANK', 'CASH', 'DIGITAL_WALLET'].includes(account.type)
  const loanUnknown = canBeLoan && !personalLoans
  // Fix balance is a main button where balances drift (bank, cash, wallets); elsewhere it's in ⋯.
  const canCorrect = isTransactable(account.type) && !isPersonalLoan && !loanUnknown
  const monthData = txPages.data
  const monthTotals =
    spendable && monthData
      ? {
          spent: monthData.spent ?? monthData.totalExpenses,
          income: monthData.income ?? monthData.totalIncome,
        }
      : null

  const menuActions: PersonAction[] = [
    { label: 'Edit', icon: Pencil, onSelect: () => setShowEditAccount(true) },
  ]
  if (account.type === 'CREDIT_CARD') {
    menuActions.push({
      label: 'Card settings',
      icon: CreditCard,
      onSelect: () => setShowCreditCardSettings(true),
    })
  }
  if (canCorrect && !spendable) {
    menuActions.push({
      label: 'Correct balance',
      icon: SlidersHorizontal,
      onSelect: () => setShowCorrectBalance(true),
    })
  }
  const reconcilable = RECONCILABLE_TYPES.includes(account.type) && !account.archivedAt
  const openReconcile = reconcilable && !isReadOnly ? () => setReconciling(true) : undefined
  if (openReconcile) {
    menuActions.push({ label: 'Reconcile', icon: CheckCheck, onSelect: openReconcile })
  }
  menuActions.push({
    label: 'Archive',
    icon: ArchiveIcon,
    onSelect: () => setConfirmArchive(true),
    destructive: true,
  })

  let actionButtons: React.ReactNode = null
  if (account.type === 'CREDIT_CARD') {
    actionButtons = (
      <>
        <button
          type="button"
          className={PRIMARY_BTN}
          onClick={() => setAdding({ kind: 'cardBill' })}
        >
          Pay card bill
        </button>
        <button
          type="button"
          className={SECONDARY_BTN}
          onClick={() => setAdding({ kind: 'spent' })}
        >
          <Plus size={15} aria-hidden="true" /> Add spend
        </button>
      </>
    )
  } else if (valued) {
    actionButtons = (
      <>
        <button type="button" className={PRIMARY_BTN} onClick={() => setShowUpdateValue(true)}>
          <RefreshCw size={15} aria-hidden="true" /> Update value
        </button>
        {account.type === 'INVESTMENT' && (
          <button
            type="button"
            className={SECONDARY_BTN}
            onClick={() => setAdding({ kind: 'transfer', transferInto: true })}
          >
            <Plus size={15} aria-hidden="true" /> Add money
          </button>
        )}
      </>
    )
  } else if (isPersonalLoan) {
    actionButtons = (
      <Link to={ROUTES.APP.PERSONAL_LOAN_DETAIL(account.id)} className={PRIMARY_BTN}>
        Manage loan
      </Link>
    )
  } else if (isTransactable(account.type)) {
    actionButtons = (
      <>
        <button type="button" className={PRIMARY_BTN} onClick={() => setAdding({})}>
          <Plus size={15} aria-hidden="true" /> Add
        </button>
        {spendable && (
          <button
            type="button"
            className={SECONDARY_BTN}
            onClick={() => setAdding({ kind: 'transfer' })}
          >
            <ArrowLeftRight size={15} aria-hidden="true" /> Transfer
          </button>
        )}
        {spendable && canCorrect && (
          <button
            type="button"
            className={SECONDARY_BTN}
            onClick={() => setShowCorrectBalance(true)}
          >
            <SlidersHorizontal size={15} aria-hidden="true" /> Fix balance
          </button>
        )}
      </>
    )
  }

  const monthName = new Date().toLocaleDateString('en-IN', { month: 'long' })
  const txCount = `${txPages.transactions.length}${txPages.hasMore ? '+' : ''}`
  const allTransactionsLink = transactionsLink(
    { ...ALL_TIME, financialAccountId: account.id },
    { back: currentPath(location) }
  )
  // A phone shows the latest few and hands the rest to the Transactions page.
  const shownPages = wide
    ? txPages
    : { ...txPages, transactions: txPages.transactions.slice(0, PHONE_TX_LIMIT), hasMore: false }
  const moreOnPhone = !wide && (txPages.hasMore || txPages.transactions.length > PHONE_TX_LIMIT)

  const goalCount = earmarks?.goals.length ?? 0
  const repeatCount =
    recurringData?.items.filter((r) => r.financialAccountId === account.id).length ?? 0
  const jumps: { id: string; label: string; icon: LucideIcon }[] = []
  if ((valued && (valuations?.length ?? 0) > 0) || (charted && (balanceHistory?.length ?? 0) > 0)) {
    jumps.push({
      id: 'account-chart',
      label: valued ? 'Value chart' : 'Balance chart',
      icon: LineChart,
    })
  }
  if (plain && goalCount > 0) {
    jumps.push({
      id: 'account-goals',
      label: `${goalCount} goal${goalCount === 1 ? '' : 's'}`,
      icon: Target,
    })
  }
  jumps.push({
    id: 'account-repeats',
    label: repeatCount > 0 ? `${repeatCount} repeat${repeatCount === 1 ? '' : 's'}` : 'Repeats',
    icon: Repeat,
  })
  jumps.push({ id: 'account-about', label: 'About', icon: Info })

  return (
    <div className="p-4 md:p-10">
      <Link
        to={ROUTES.APP.ACCOUNTS}
        className="mb-3 inline-flex h-9 items-center gap-1.5 px-1 text-[13px] text-[var(--fl-text-2)] hover:text-[var(--fl-heading)]"
      >
        <ArrowLeft size={14} aria-hidden="true" /> Accounts
      </Link>

      <AccountHero
        account={account}
        owners={ownerSummary(ownerNames, members.length)}
        earmarks={earmarks}
        monthTotals={monthTotals}
        latestValuation={valued ? (valuations ? (valuations[0] ?? null) : undefined) : undefined}
        menu={!isReadOnly && <PersonActionsMenu name={account.name} actions={menuActions} />}
        preview={!wide && charted ? <BalancePreview account={account} /> : undefined}
        actions={
          !isReadOnly &&
          actionButtons && (
            <div className="grid auto-cols-fr grid-flow-col gap-2 lg:grid-flow-row lg:grid-cols-1">
              {actionButtons}
            </div>
          )
        }
      />

      {!wide && (
        <nav
          aria-label="Jump to"
          className="-mx-4 mt-3 flex gap-1.5 overflow-x-auto px-4 [scrollbar-width:none]"
        >
          {reconcilable && <ReconcileChip account={account} onOpen={openReconcile} />}
          {jumps.map(({ id: target, label, icon: Icon }) => (
            <button
              key={target}
              type="button"
              onClick={() =>
                document
                  .getElementById(target)
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
              className="flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-[var(--fl-border)] bg-[var(--fl-surface)] px-3 text-[13px] font-medium text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]"
            >
              <Icon size={13} aria-hidden="true" />
              {label}
              <ArrowDown size={12} aria-hidden="true" className="text-[var(--fl-text-3)]" />
            </button>
          ))}
        </nav>
      )}

      {/* Phone: one column with transactions first (left column's children join the grid via
          `contents`); desktop: the two columns as before. */}
      <div className="grid grid-cols-1 gap-x-7 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="contents lg:block">
          <section aria-labelledby="account-transactions" className="order-1 lg:order-none">
            <SectionHeader
              title={`${monthName} · ${txCount} transactions`}
              right={
                <span className="flex items-center gap-3">
                  {wide && reconcilable && (
                    <ReconcileChip account={account} onOpen={openReconcile} />
                  )}
                  <Link
                    to={allTransactionsLink}
                    className="text-[13px] font-medium text-[var(--fl-primary)] hover:underline"
                  >
                    See all →
                  </Link>
                </span>
              }
            />
            <span id="account-transactions" className="sr-only">
              Transactions this month
            </span>
            <TransactionList
              pages={shownPages}
              hasFilters={false}
              compact
              focusAccountId={account.id}
            />
            {moreOnPhone && (
              <Link
                to={allTransactionsLink}
                className="mt-3 flex h-10 items-center justify-center rounded-full border border-[var(--fl-border)] bg-[var(--fl-surface)] text-[13px] font-semibold text-[var(--fl-primary)]"
              >
                See all {txCount} transactions →
              </Link>
            )}
          </section>
          <div id="account-about" className="order-3 scroll-mt-4 lg:order-none">
            <AboutAccount account={account} ownerNames={ownerNames} />
          </div>
        </div>

        <div className="order-2 lg:order-none">
          <div id="account-chart" className="scroll-mt-4">
            {valued ? (
              <ValueChart account={account} />
            ) : isTransactable(account.type) ? (
              <BalanceChart account={account} />
            ) : null}
          </div>
          {account.type === 'CREDIT_CARD' && <BillCategories account={account} />}
          <div id="account-goals" className="scroll-mt-4">
            {plain && <AccountGoals accountId={account.id} />}
          </div>
          {valued && <ValueUpdates accountId={account.id} />}
          <div id="account-repeats" className="scroll-mt-4">
            <AccountRepeats account={account} onAdd={() => setShowAddRecurring(true)} />
          </div>
        </div>
      </div>

      {/* Modals — reconcile first, so "Add missing" opens the add form on top of it */}
      {reconciling && (
        <ReconcileModal
          account={account}
          onAddMissing={() => setAdding({})}
          onClose={() => setReconciling(false)}
        />
      )}
      {adding && (
        <AddTransactionModal
          accounts={data?.accounts ?? []}
          categories={categoriesData?.categories ?? []}
          initialAccountId={account.id}
          initialKind={adding.kind}
          transferInto={adding.transferInto}
          onClose={() => setAdding(null)}
        />
      )}

      {showEditAccount && (
        <EditAccountModal account={account} onClose={() => setShowEditAccount(false)} />
      )}

      {showCorrectBalance && (
        <CorrectBalanceModal account={account} onClose={() => setShowCorrectBalance(false)} />
      )}

      {showCreditCardSettings && (
        <CreditCardSettingsModal
          account={account}
          onClose={() => setShowCreditCardSettings(false)}
        />
      )}

      {showUpdateValue && (
        <UpdateValueModal account={account} onClose={() => setShowUpdateValue(false)} />
      )}

      {showAddRecurring && (
        <RecurringModal
          defaultType="EXPENSE"
          defaultFinancialAccountId={account.id}
          onClose={() => setShowAddRecurring(false)}
        />
      )}

      <ArchiveAccountDialog
        account={confirmArchive ? account : null}
        onClose={() => setConfirmArchive(false)}
      />
    </div>
  )
}
